import { useMemo } from 'react';
import { InboxLocationPhones } from '@frontend/inbox-location-phones';
import { useMessagingAIRCustomizationFlag } from '@frontend/shared';

/**
 * AI Status from backend thread data
 */
type BackendAIStatus = {
  isActive?: boolean;
  isSeeded?: boolean;
  isBotDeactivatedByPhoneConfig?: boolean;
};

/**
 * Computed AI status based on the spoofing logic with LocationPhone AI check and seeded flag.
 *
 * Priority System:
 * 1. LocationPhone AI = false → HIDE UI, call SetBotInactive to reset
 * 2. Seeded + locationPhone AI = true + active → ACTIVE
 * 3. Seeded + locationPhone AI = true + inactive + isBotDeactivatedByPhoneConfig → Re-activate (spoofed)
 * 4. Seeded + locationPhone AI = true + inactive + not deactivated by phone config → Stay INACTIVE
 * 5. Unseeded + locationPhone AI = true → Spoof as ACTIVE, trigger initialization
 *
 */
export type EffectiveAIStatus = {
  /** Whether the bot should be shown as ACTIVE in the UI */
  isActive: boolean;
  /** Whether the bot UI should be shown at all */
  shouldShowUI: boolean;
  /** Whether initialization is needed (unseeded + AI phone) */
  needsInitialization: boolean;
  /** Whether reset is needed (seeded + active + non-AI phone) */
  needsReset: boolean;
  /** Whether reactivation is needed (seeded + inactive via phone config change + AI phone restored) */
  needsReactivation: boolean;
};

type UseEffectiveAIStatusProps = {
  /** AI status from thread data */
  aiStatus?: BackendAIStatus;
  /** Location phone for this thread */
  locationPhone?: string;
  /** Location ID for this thread */
  locationId: string;
};

/**
 * Hook to compute AI status based on seeded flag and locationPhone AI configuration.
 *
 * Decision Matrix:
 * | isSeeded | isActive | deactivatedByPhoneCfg | LocationPhone AI? | Bot Status                | Bot UI?  |
 * |----------|----------|-----------------------|-------------------|---------------------------|----------|
 * | true     | true     | any                   | true              | ACTIVE                    | Yes      |
 * | true     | true     | any                   | false             | HIDDEN → SetBotInactive   | HIDDEN   |
 * | true     | false    | true                  | true              | ACTIVE (reactivation)     | Yes      |
 * | true     | false    | false                 | true              | INACTIVE (user intent)    | Yes      |
 * | true     | false    | any                   | false             | HIDDEN                    | HIDDEN   |
 * | false    | false    | any                   | true              | ACTIVE (spoofed)          | Yes      |
 * | false    | false    | any                   | false             | HIDDEN                    | HIDDEN   |
 */
export const useEffectiveAIStatus = ({
  aiStatus,
  locationPhone,
  locationId,
}: UseEffectiveAIStatusProps): EffectiveAIStatus => {
  const { isActive: isFusionEnabled } = useMessagingAIRCustomizationFlag([locationId]);

  const { checkIsLocationPhoneAiEnabled } = InboxLocationPhones.Hooks.useLocationPhones();

  const effectiveStatus = useMemo(() => {
    // Guard: Only execute logic when BOTH locationPhone and aiStatus are available
    if (!locationPhone || !aiStatus) {
      return {
        isActive: false,
        shouldShowUI: false,
        needsInitialization: false,
        needsReset: false,
        needsReactivation: false,
      };
    }

    const persistedIsActive = aiStatus.isActive ?? false;

    // Check if the thread's locationPhone is AI-enabled
    const isLocationPhoneAiEnabled = checkIsLocationPhoneAiEnabled(locationPhone);

    if (!isFusionEnabled) {
      return {
        isActive: persistedIsActive,
        shouldShowUI: false,
        needsInitialization: false,
        needsReset: false,
        needsReactivation: false,
      };
    }

    const persistedIsSeeded = aiStatus?.isSeeded ?? false;

    // PRIORITY 1: LocationPhone AI = false → HIDE UI, reset if actively running
    if (!isLocationPhoneAiEnabled) {
      return {
        isActive: false,
        shouldShowUI: false,
        needsInitialization: false,
        // Need reset only if thread was seeded AND active (to deactivate via PHONE_CONFIG_CHANGE)
        needsReset: persistedIsSeeded && persistedIsActive,
        needsReactivation: false,
      };
    }

    // From here: LocationPhone AI = true

    // Case: Seeded thread (LocationPhone AI = true, isSeeded = true)
    if (persistedIsSeeded) {
      // Check if bot was deactivated by phone config change (not by user)
      // If so, we should reactivate it since the AI phone is back
      const wasDeactivatedByPhoneConfig = aiStatus?.isBotDeactivatedByPhoneConfig ?? false;

      if (wasDeactivatedByPhoneConfig) {
        // Spoof as ACTIVE and trigger reactivation
        return {
          isActive: true,
          shouldShowUI: true,
          needsInitialization: false,
          needsReset: false,
          needsReactivation: true,
        };
      }

      // isActive=true → ACTIVE, isActive=false → INACTIVE (user intent preserved)
      return {
        isActive: persistedIsActive,
        shouldShowUI: true,
        needsInitialization: false,
        needsReset: false,
        needsReactivation: false,
      };
    }

    // Case: Unseeded thread (LocationPhone AI = true, isSeeded = false)
    // Both isActive=false and isActive=true should show as ACTIVE
    // This is always a spoof state until we call SetBotActive to seed the thread
    return {
      isActive: true,
      shouldShowUI: true,
      // Need initialization to seed the thread with SetBotActive
      needsInitialization: true,
      needsReset: false,
      needsReactivation: false,
    };
  }, [aiStatus, locationPhone, locationId, checkIsLocationPhoneAiEnabled, isFusionEnabled]);

  return effectiveStatus;
};
